/**
 * Custom hook for fetching products from Firestore
 */

import { useState, useEffect, useCallback } from 'react';
import { getProductsByForm, getAllProductsWithStatus, Product } from '../services/ProductService';

export interface UseProductsResult {
  products: Product[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch products by ItemForm
 */
export function useProducts(itemForm: 'Vegetable' | 'Flower' | 'Heritage' | 'Livingston'): UseProductsResult {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log(`🔄 Fetching ${itemForm} products from Firestore...`);
      const fetchedProducts = await getProductsByForm(itemForm);
      console.log(`✅ Successfully loaded ${fetchedProducts.length} ${itemForm} products`);
      
      if (fetchedProducts.length === 0) {
        console.warn(`⚠️ No ${itemForm} products found in Firestore. Make sure you've run: npm run upload:products`);
        setError(`No ${itemForm.toLowerCase()} products found. Please run 'npm run upload:products' to populate the database.`);
      } else {
        setProducts(fetchedProducts);
      }
    } catch (err: any) {
      console.error(`❌ Error fetching ${itemForm} products:`, err);
      // Provide more detailed error message
      let errorMessage = `Failed to load ${itemForm.toLowerCase()} products. `;
      
      if (err?.code === 'permission-denied') {
        errorMessage += 'Permission denied. Please check Firestore security rules.';
      } else if (err?.code === 'failed-precondition') {
        errorMessage += 'Firestore index required. Please create a composite index for ItemForm and ItemCode.';
      } else if (err?.code === 'not-found') {
        errorMessage += 'Products collection not found. Please run: npm run upload:products';
      } else if (err?.message) {
        errorMessage += `Error: ${err.message}`;
      } else {
        errorMessage += 'Please check the console for details.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [itemForm]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return {
    products,
    loading,
    error,
    refetch: fetchProducts,
  };
}

/**
 * Hook that returns a Set of ItemCodes that are currently out-of-stock.
 * Designed for forms that use static product lists (Flower, Heritage, Livingston).
 * Returns an empty Set while loading so no products are incorrectly blocked.
 */
export function useOutOfStockCodes(): { outOfStockCodes: Set<string>; loading: boolean } {
  const [outOfStockCodes, setOutOfStockCodes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const products = await getAllProductsWithStatus();
        if (!cancelled) {
          const codes = new Set(
            products
              .filter(p => p.status === 'out-of-stock')
              .map(p => p.ItemCode)
          );
          setOutOfStockCodes(codes);
        }
      } catch (err) {
        console.error('useOutOfStockCodes: failed to fetch product statuses', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { outOfStockCodes, loading };
}



